import { Download, Search } from 'lucide-react'

import { viewOptions, type ViewOptionsType } from '@/pages/Dashboard'
import useBreakpoints from '@/hooks/useBreakpoints'
import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import FormInput from '@/elements/FormInput'
import FormSelect from '@/elements/FormSelect'
import RadioGroup from '@/elements/RadioGroup'
import { formatAsTitle } from '@/utils/format'
import type { SortingDirType } from '@/api/request'
import type { SubjectGroupResponse } from '@/api/types/Subject'
import useSortOptions, { type SortWithDirection } from '@/hooks/useSortOptions'

interface FilterBarProps {
  view: ViewOptionsType
  onViewChange: (view: ViewOptionsType) => void
  filterByName: string
  setFilterByName: (name: string) => void
  filterByGroupName?: string
  setFilterByGroupName?: (groupName: string) => void
  groupOptions?: SubjectGroupResponse[]
  sortDir?: SortingDirType
  sortBy?: string
  setSortBy?: (sortBy: string, dir?: SortingDirType) => void
  sortByOptions?: string[]
  onExport?: () => void
}

function FilterBar({
  view,
  onViewChange,
  filterByName,
  setFilterByName,
  filterByGroupName,
  setFilterByGroupName,
  groupOptions,
  sortDir,
  sortBy,
  setSortBy,
  sortByOptions,
  onExport,
}: FilterBarProps) {
  const { isTablet, isMobile } = useBreakpoints()
  const { sortOptions, selectedSortOption } = useSortOptions({
    sortByOptions,
    sortBy,
    sortDir,
  })

  return (
    <Flex
      wfull
      inline={!isMobile}
      justify='space-between'
      align='end'
      wrap
      gap={4}
      gv={3}
    >
      <Flex inline align='end' gap={3} grow={isTablet} wrap={isMobile} wfull={isMobile}>
        <FormSelect<SortWithDirection>
          allowUnselect
          optionsAutoWidth
          values={selectedSortOption ? [selectedSortOption] : []}
          onChange={([v]) => setSortBy?.(v?.value, v?.dir)}
          optionLabel={item => formatAsTitle(item.value) + ` ${item.dirLabel}`}
          optionValue={item => `${item.value}-${item.dir}`}
          name='sort-by'
          label='Sort by'
          placeholder='Default'
          style={{ width: 186, flex: isTablet ? 1 : undefined }}
          options={sortOptions}
          dirty={!!sortBy}
        />
        <FormSelect<SubjectGroupResponse>
          allowUnselect
          optionsAutoWidth
          name='filter-by'
          label='Filter by'
          placeholder='Group Name'
          values={Object.values(groupOptions || {}).filter(option => option.name === filterByGroupName)}
          onChange={([v]) => setFilterByGroupName?.(v?.name)}
          options={
            groupOptions ? groupOptions.filter(option => option.name && option.name.length > 0) : []
          }
          optionLabel={item => item.name}
          optionValue={item => item.name}
          style={{ width: 226, flex: isTablet ? 1 : undefined }}
          dirty={!!filterByGroupName}
          filterBy={(option, search) => option.name.toLowerCase().includes(search.toLowerCase())}
        />
      </Flex>
      <Flex inline align='end' gap={3} grow={isTablet} wfull={isMobile}>
        <FormInput
          name='search'
          placeholder='Search Individuals...'
          Icon={Search}
          value={filterByName}
          onChangeDebounced={val => setFilterByName(val)}
          dirty={!!filterByName}
          style={{ flex: 1 }}
        />
        {!isMobile && (
          <>
            <RadioGroup
              value={view}
              onChange={value => onViewChange(value as ViewOptionsType)}
              name='experience-level'
              options={viewOptions}
            />
            {onExport && (
              <Button
                variant='iconBtn'
                onClick={onExport}
                styles={{ width: 54, height: 54, minWidth: 54, minHeight: 54 }}
              >
                <Download size={20} />
              </Button>
            )}
          </>
        )}
      </Flex>
    </Flex>
  )
}

export default FilterBar
